import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { Card, CardTitle, CardActions } from 'material-ui/Card';
import { TextInput, NumberInput } from 'admin-on-rest/lib/mui';
import JSONSchemaForm from 'react-jsonschema-form';
import { RaisedButton, Dialog, FontIcon } from 'material-ui';
import EditIcon from 'material-ui/svg-icons/image/edit';
import ForwardIcon from 'material-ui/svg-icons/navigation/arrow-forward';
import BackIcon from 'material-ui/svg-icons/navigation/arrow-back';
import omit from 'lodash.omit';
import pick from 'lodash.pick';
import { log } from '../helpers/log';
import { VALIDATOR_OPTIONS } from '../helpers/validators';
import { DragSource, DropTarget } from 'react-dnd';
import { DRAG_TYPE_FIELD } from '../constants';

const FORM_SCHEMA_EDIT_FIELD = {
  schema: {
    title: 'Edit field',
    type: 'object',
    required: [],
    properties: {
      csvHeader: {
        type: 'string',
        title: 'CSV Header'
      },
      title: {
        title: 'Label',
        type: 'string'
      },
      description: {
        title: 'Description',
        type: 'string'
      },
      visible: {
        title: 'Visible',
        type: 'boolean'
      },
      readonly: {
        title: 'Read only',
        type: 'boolean'
      },
      required: {
        title: 'Required',
        type: 'boolean'
      },
      dataType: {
        title: 'Type',
        type: 'string',
        enum: [
          'string',
          'text',
          'phone_number',
          'number',
          // 'range',
          'boolean',
          'date',
          'datetime',
          'select',
          'select_with_values',
          'checkbox'
        ]
      },
      validators: {
        title: 'Validators',
        type: 'array',
        items: {
          type: 'string',
          enum: VALIDATOR_OPTIONS
        }
      }
    },
    dependencies: {
      dataType: {
        oneOf: [
          {
            properties: {
              dataType: {
                enum: ['text']
              },
              pattern: {
                title: 'Pattern',
                type: 'string'
              }
            }
          },
          {
            properties: {
              dataType: {
                enum: ['date']
              },
              dateFormat: {
                title: 'Date format',
                /*
https://flatpickr.js.org/formatting/
d	Day of the month, 2 digits with leading zeros	01 to 31
D	A textual representation of a day	Mon through Sun
l (lowercase 'L')	A full textual representation of the day of the week	Sunday through Saturday
j	Day of the month without leading zeros	1 to 31
J	Day of the month without leading zeros and ordinal suffix	1st, 2nd, to 31st
w	Numeric representation of the day of the week	0 (for Sunday) through 6 (for Saturday)
F	A full textual representation of a month	January through December
m	Numeric representation of a month, with leading zero	01 through 12
n	Numeric representation of a month, without leading zeros	1 through 12
M	A short textual representation of a month	Jan through Dec
U	The number of seconds since the Unix Epoch	1413704993
y	A two digit representation of a year	99 or 03
Y	A full numeric representation of a year, 4 digits	1999 or 2003
Z	ISO Date format	2017-03-04T01:23:43.000Z

H	Hours (24 hours)	00 to 23
h	Hours	1 to 12
i	Minutes	00 to 59
S	Seconds, 2 digits	00 to 59
s	Seconds	0, 1 to 59
K	AM/PM	AM or PM

                */
                description: `Examples:
                d/m/Y for dates like 21/02/1998,
                m/d/Y for dates like 02/25/1998,
                y/m/d for dates like 92/02/23

                d - day of the month with leading zero 01 - 31
                j - day of the month with leading zero 1 - 31
                m - month with leading zero 01 - 12
                n - month with leading zero 1 - 12
                y - year short 2 digits like 70, 99, 04
                Y - full year 4 digits like 1980, 2021

                H -	Hours (24 hours)	00 to 23
                i	- Minutes	00 to 59
                S	- Seconds, 2 digits	00 to 59
                `,
                type: 'string'
              }
            }
          },
          {
            properties: {
              dataType: {
                enum: ['datetime']
              },
              dateFormat: {
                title: 'Date format',
                type: 'string'
              }
            }
          },
          {
            properties: {
              dataType: {
                enum: ['checkbox']
              },
              values: {
                title: 'Options',
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            }
          },
          {
            properties: {
              dataType: {
                enum: ['select']
              },
              values: {
                title: 'Options',
                type: 'array',
                items: {
                  type: 'string'
                }
              }
            }
          },
          {
            properties: {
              dataType: {
                enum: ['select_with_values']
              },
              values: {
                title: 'Options',
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    label: {
                      type: 'string'
                    },
                    value: {
                      type: 'string'
                    }
                  }
                }
              }
            }
          }
        ]
      }
    }
  },
  uiSchema: {
    csvHeader: {
      'ui:readonly': true
    },
    description: {
      'ui:widget': 'textarea'
    }
  }
};

class EditableFormField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      editMode: false
    };
  }

  getFormDataFromProps(props) {
    return {
      ...pick(
        props || {},
        Object.keys(FORM_SCHEMA_EDIT_FIELD.schema.properties).concat([
          'pattern',
          'dateFormat',
          'values'
        ])
      )
    };
  }

  _toggleEditMode = (...args) => {
    this.setState({ editMode: !this.state.editMode });
  };

  _onSubmit = ({ formData }) => {
    this.setState({ editMode: false });
    if (this.props.onChange) {
      this.props.onChange(this.props.identifier, {
        ...formData,
        at: Date.now(),
        editableFormField: true
      });
    }
  };

  _onForward = () => {
    this.props.onForward(this.props.identifier);
  };

  _onBack = () => {
    this.props.onBack(this.props.identifier);
  };

  _onFieldChange = (...args) => {
  };

  render() {
    const { schema, uiSchema } = FORM_SCHEMA_EDIT_FIELD;
    const editMode = this.state.editMode;
    return this.props.connectDragSource(
      this.props.connectDropTarget(
        <div
          className={`editable-field ${
            this.props.hidden ? 'field-hidden' : ''
          } ${this.props.isDragging ? 'field-dragged' : ''} ${
            this.props.isOver ? 'field-drag-over' : ''
          }`}
        >
          {!editMode &&
            this.props.editable && (
              <div className="edit-button">
                <RaisedButton
                  onClick={this._toggleEditMode}
                  title="Edit"
                  label=""
                  icon={<EditIcon />}
                />
                <RaisedButton
                  onClick={this._onBack}
                  title=""
                  label=""
                  icon={<BackIcon />}
                />
                <RaisedButton
                  onClick={this._onForward}
                  title=""
                  label=""
                  icon={<ForwardIcon />}
                />
              </div>
            )}
          <Field
            name={this.props.identifier}
            component={this.props.component}
            label={this.props.label}
            {...this.props}
          />
          <Dialog
            open={editMode}
            onRequestClose={this._toggleEditMode}
            autoScrollBodyContent={true}
          >
            <div className="bootstrap field-editor">
              <JSONSchemaForm
                schema={schema}
                uiSchema={uiSchema}
                formData={this.props.formData}
                onChange={this._onFieldChange}
                onSubmit={this._onSubmit}
                onError={log('errors - editor')}
              />
            </div>
          </Dialog>
        </div>
      )
    );
  }
}

EditableFormField.propTypes = {
  component: PropTypes.any,
  onChange: PropTypes.func,
  onBack: PropTypes.func,
  onForward: PropTypes.func,
  identifier: PropTypes.string,
  label: PropTypes.string,
  formData: PropTypes.object,
  editable: PropTypes.bool,
  hidden: PropTypes.bool
};

export default DragSource(
  DRAG_TYPE_FIELD,
  {
    beginDrag: props => ({
      identifier: props.identifier
    })
  },
  (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging()
  })
)(
  DropTarget(
    DRAG_TYPE_FIELD,
    {
      drop(props, monitor, component) {
        if (monitor.didDrop()) {
          return;
        }

        // Obtain the dragged item
        const item = monitor.getItem();

        props.onDropOnIdentifier(
          item.identifier || 'unknown_item',
          props.identifier
        );
        return { moved: true };
      }
    },
    (connect, monitor) => ({
      connectDropTarget: connect.dropTarget(),
      isOver: monitor.isOver()
    })
  )(EditableFormField)
);



// WEBPACK FOOTER //
// ./src/components/EditableFormField.js